import { resolveNfcCapabilities } from '../../../utils/resolveNfcCapabilities';
import { supportsGoogleContactlessMode } from '../../../utils/googleNfc';
import NfcPlatformNotice from '../NfcPlatformNotice';
import SectionWrapper from './SectionWrapper';
import { useAppSelector } from '../../../store/redux/hooks';
import {
  Flex,
  FormControl,
  FormLabel,
  Select,
  Input,
  Collapse,
  FormErrorMessage,
  Text,
  Link,
  Alert,
  AlertIcon,
  Box,
  Divider,
} from '@chakra-ui/react';
import SwitchField from './SwitchField';
import { useAppDispatch } from '../../../store/redux/hooks';
import {
  setNfcFeature,
  setBarcodeFeature,
  setAppleEnhancedDesignType,
} from '../../../store/redux/slices/template.slice';
import { clearNamespaceErrors } from '../../../store/redux/slices/templateErrors.slice';
import { APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE } from './utils/appleEnhancedDesignConstants';
import { designRequiresTemplateNfc } from './utils/enhancedDesignEligibility';
import { LabelWithTooltip } from './LabelWithTooltip';
import { useTemplateFieldValidation } from './validation/useTemplateFieldValidation';
import { NfcFeature, QrFeature } from '../../../store/types/template';
import { MeContext } from '../../../context/MeContext';
import { MeContextState } from '../../../store/types/account';
import { useContext, useRef, useState, useEffect, useMemo } from 'react';
import { setSimpleField } from '../../../store/redux/slices/template.slice';
import OverrideRadio from './OverrideRadio';
import FieldEditIndicator from './FieldEditIndicator';
import { useFieldEditIndicator } from './hooks/useFieldEditIndicator';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';

const ScanningFeature = ({
  onNavigateToEnhancedTicket,
  cameFromEnhancedTicket,
  isEdit,
}: {
  onNavigateToEnhancedTicket?: () => void;
  cameFromEnhancedTicket?: boolean;
  isEdit?: boolean;
}) => {
  const meCtx = useContext(MeContext) as { state: MeContextState };
  const entity =
    meCtx.state.entities?.length === 1
      ? meCtx.state.entities[0]
      : meCtx.state.filterEntity;
  const {
    nfcAvailable,
    googleNfcAvailable,
    appleNfcAvailable,
    googleOnly,
    isResolved,
  } = resolveNfcCapabilities(entity);

  const dispatch = useAppDispatch();
  const nfc = useAppSelector((s) => s.template.nfc);
  const barcode = useAppSelector((s) => s.template.barcode);
  const appleEnhancedDesignType = useAppSelector(
    (s) => s.template.appleEnhancedDesign.type
  );
  const templateType = useAppSelector((s) => s.template.templateType);
  const showGoogleNfc = supportsGoogleContactlessMode(templateType);
  const showContactlessWarning =
    showGoogleNfc && googleNfcAvailable && nfc.enabled && !nfc.googleNfc;
  const originalNfc = useAppSelector((s) => s.template._originalNfc);
  const originalBarcode = useAppSelector((s) => s.template._originalBarcode);
  const reduxOverrideNfc = useAppSelector((s) => s.template._overrideNfc);
  const reduxOverrideBarcode = useAppSelector(
    (s) => s.template._overrideBarcode
  );
  const [overrideNfc, setOverrideNfc] = useState<'new' | 'all'>(
    reduxOverrideNfc ? 'all' : 'new'
  );
  const [overrideBarcode, setOverrideBarcode] = useState<'new' | 'all'>(
    reduxOverrideBarcode ? 'all' : 'new'
  );

  const nfcChanged =
    isEdit &&
    (nfc.enabled !== originalNfc.enabled ||
      nfc.source !== originalNfc.source ||
      nfc.customValue !== originalNfc.customValue);

  const barcodeChanged = useMemo(() => {
    if (!isEdit) return false;
    return (
      barcode.enabled !== originalBarcode.enabled ||
      barcode.type !== originalBarcode.type ||
      barcode.source !== originalBarcode.source ||
      barcode.value !== originalBarcode.value ||
      barcode.displayText !== originalBarcode.displayText
    );
  }, [isEdit, barcode, originalBarcode]);

  useEffect(() => {
    if (!nfcChanged) setOverrideNfc('new');
  }, [nfcChanged]);

  useEffect(() => {
    if (!barcodeChanged) setOverrideBarcode('new');
  }, [barcodeChanged]);

  useEffect(() => {
    dispatch(
      setSimpleField({ field: '_overrideNfc', value: overrideNfc === 'all' })
    );
  }, [overrideNfc, dispatch]);

  useEffect(() => {
    dispatch(
      setSimpleField({
        field: '_overrideBarcode',
        value: overrideBarcode === 'all',
      })
    );
  }, [overrideBarcode, dispatch]);

  const nfcEnabledRef = useRef<HTMLDivElement>(null);
  const nfcSourceRef = useRef<HTMLDivElement>(null);
  const nfcCustomValueRef = useRef<HTMLDivElement>(null);
  const nfcGoogleNfcRef = useRef<HTMLDivElement>(null);
  const barcodeEnabledRef = useRef<HTMLDivElement>(null);
  const barcodeTypeRef = useRef<HTMLDivElement>(null);
  const barcodeSourceRef = useRef<HTMLDivElement>(null);
  const barcodeValueRef = useRef<HTMLDivElement>(null);
  const barcodeDisplayTextRef = useRef<HTMLDivElement>(null);

  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    fieldRefMap: {
      'nfc.enabled': nfcEnabledRef,
      'nfc.source': nfcSourceRef,
      'nfc.customValue': nfcCustomValueRef,
      'nfc.googleNfc': nfcGoogleNfcRef,
      'barcode.enabled': barcodeEnabledRef,
      'barcode.type': barcodeTypeRef,
      'barcode.source': barcodeSourceRef,
      'barcode.value': barcodeValueRef,
      'barcode.displayText': barcodeDisplayTextRef,
    },
  });

  const sources = [
    { label: 'UUID', value: 'uuid' },
    { label: 'Ext ID', value: 'extId' },
    { label: 'Custom', value: 'custom' },
  ];

  const barcodeTypes = [
    { label: 'QR', value: 'qr' },
    { label: 'AZTEC', value: 'aztec' },
    { label: 'PDF417', value: 'pdf417' },
    { label: 'CODE128', value: 'code128' },
  ];

  const { handleFocus, handleBlur } = useTemplateFieldValidation();

  const {
    validateField: validateNfcField,
    validateSection: validateNfcSection,
  } = useTemplateFieldValidation('nfc');
  const errors = useAppSelector((s) => s.templateErrors);

  const showReturnToEnhancedTicket =
    templateType === 'eventTicket' &&
    appleNfcAvailable &&
    nfc.enabled &&
    !!cameFromEnhancedTicket &&
    !!onNavigateToEnhancedTicket;

  const {
    validateField: validateBarcodeField,
    validateSection: validateBarcodeSection,
  } = useTemplateFieldValidation('barcode');

  const handleChange = (
    feature: 'nfc' | 'barcode',
    field: keyof NfcFeature | keyof QrFeature,
    value: any
  ) => {
    if (feature === 'nfc') {
      const updates: Partial<NfcFeature> = { [field]: value };
      if (field === 'source' && value !== 'custom') {
        updates.customValue = '';
      }
      const updatedNfc = { ...nfc, ...updates };
      dispatch(setNfcFeature(updates));

      if (
        field === 'enabled' &&
        value === false &&
        designRequiresTemplateNfc(appleEnhancedDesignType)
      ) {
        dispatch(setAppleEnhancedDesignType(null));
        dispatch(clearNamespaceErrors(APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE));
      }

      validateNfcField(field, value, { nfc: updatedNfc });

      if (
        (field === 'enabled' && value === false) ||
        (field === 'source' && value !== 'custom')
      ) {
        validateNfcSection(updatedNfc);
      }
    }

    if (feature === 'barcode') {
      const updates: Partial<QrFeature> = { [field]: value };
      if (field === 'source' && value !== 'custom') {
        updates.value = '';
      }
      const updatedBarcode = { ...barcode, ...updates };
      dispatch(setBarcodeFeature(updates));

      validateBarcodeField(field, value, { barcode: updatedBarcode });

      if (
        (field === 'enabled' && value === false) ||
        (field === 'source' && value !== 'custom')
      ) {
        validateBarcodeSection(updatedBarcode);
      }
    }
  };

  return (
    <SectionWrapper
      id="nfc-qr-section"
      title="Scanning"
      tooltip="Let users scan their NFC pass for instant identification – you can use the unique UUID of the pass, external ID or a custom value."
      openOnLoad={true}
    >
      <Flex direction="column" gap={6}>
        <Flex direction="column" gap={4} mt={2}>
          {showGoogleNfc && googleNfcAvailable && (
            <FieldEditIndicator
              fieldRef={nfcGoogleNfcRef}
              isFieldBeingEdited={isFieldBeingEdited}
              activeFieldRef={activeFieldRef}
              left="-40px"
              top="55px"
            >
              <FormControl
                isInvalid={!!errors[`nfc.googleNfc`]}
                onClick={() => handleFocus('nfc.googleNfc')}
              >
                <SwitchField
                  value={nfc.googleNfc}
                  onChange={(value) => {
                    handleChange('nfc', 'googleNfc', value);
                    handleFocus('nfc.googleNfc');
                  }}
                  label={
                    <LabelWithTooltip
                      label="Enable Google NFC"
                      tooltip="Enable this to use Google Smart Tap – if you use both NFC and barcode, barcode will be displayed on the back of the pass."
                    />
                  }
                />
                {errors[`nfc.googleNfc`] && (
                  <FormErrorMessage
                    pl={2}
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                  >
                    <CustomErrorIcon />
                    {errors[`nfc.googleNfc`]}
                  </FormErrorMessage>
                )}
              </FormControl>
            </FieldEditIndicator>
          )}
          <Box mt={showGoogleNfc ? 1 : 0}>
            <FieldEditIndicator
              fieldRef={nfcEnabledRef}
              isFieldBeingEdited={isFieldBeingEdited}
              activeFieldRef={activeFieldRef}
              left="-40px"
              top="50px"
            >
              <FormControl
                isInvalid={!!errors[`nfc.enabled`]}
                onClick={() => handleFocus('nfc.enabled')}
              >
                <SwitchField
                  value={nfc.enabled}
                  onChange={(value) => handleChange('nfc', 'enabled', value)}
                  disabled={!nfcAvailable}
                  label={
                    <FormLabel variant="templateBuilderLabel" mb={0}>
                      Enable NFC
                    </FormLabel>
                  }
                />
                {showContactlessWarning && (
                  <Alert
                    status="warning"
                    bg="warningOrangeLight"
                    borderRadius="8px"
                    fontSize="14px"
                    mt={2}
                  >
                    <AlertIcon color="warningOrange" />
                    <Box>
                      <Text fontWeight="semibold">
                        NFC won’t appear on Google passes.
                      </Text>
                      <Link
                        as="button"
                        type="button"
                        textDecoration="underline"
                        onClick={() => handleChange('nfc', 'googleNfc', true)}
                      >
                        Turn on Google NFC
                      </Link>{' '}
                      to enable it.
                      {googleOnly && (
                        <Text mt={2}>
                          Apple NFC requires approval.{' '}
                          <Link
                            href="https://docs.passentry.com/documentation/guides/nfc-passes/guide"
                            isExternal
                            color="linkBlue"
                          >
                            Find out more →
                          </Link>
                        </Text>
                      )}
                    </Box>
                  </Alert>
                )}
                {googleOnly && nfc.enabled && !showContactlessWarning && (
                  <NfcPlatformNotice />
                )}
                {isResolved && !nfcAvailable && (
                  <>
                    <Text color="orangePrimary" fontSize="16px" mt={4} mb={2}>
                      NFC access is restricted.
                    </Text>
                    <Text>
                      Contact{' '}
                      <Link
                        href="mailto:support@passentry.com"
                        textDecoration="underline"
                      >
                        support@passentry.com
                      </Link>{' '}
                      to request access.
                    </Text>
                  </>
                )}
                {errors[`nfc.enabled`] && (
                  <FormErrorMessage
                    pl={2}
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                  >
                    <CustomErrorIcon />
                    {errors[`nfc.enabled`]}
                  </FormErrorMessage>
                )}
              </FormControl>
            </FieldEditIndicator>
          </Box>
          {showReturnToEnhancedTicket && (
            <Alert
              status="success"
              bg="rgba(29, 190, 104, 0.1)"
              borderRadius="8px"
              fontSize="14px"
            >
              <AlertIcon color="success" />
              <Box>
                NFC enabled. Return to{' '}
                <Link
                  onClick={onNavigateToEnhancedTicket}
                  textDecoration="underline"
                  cursor="pointer"
                >
                  enhanced ticket
                </Link>
              </Box>
            </Alert>
          )}
          <Collapse in={nfc.enabled}>
            <Flex direction="column" gap={4}>
              <FieldEditIndicator
                fieldRef={nfcSourceRef}
                isFieldBeingEdited={isFieldBeingEdited}
                activeFieldRef={activeFieldRef}
                left="-40px"
                top="54px"
              >
                <FormControl
                  isInvalid={!!errors[`nfc.source`]}
                  onFocus={() => handleFocus('nfc.source')}
                >
                  <FormLabel variant="templateBuilderLabel">Source</FormLabel>
                  <Select
                    value={nfc.source}
                    onChange={(value) =>
                      handleChange('nfc', 'source', value.target.value)
                    }
                  >
                    {sources.map((source) => (
                      <option key={source.value} value={source.value}>
                        {source.label}
                      </option>
                    ))}
                  </Select>
                  {errors[`nfc.source`] && (
                    <FormErrorMessage
                      pl={2}
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                    >
                      <CustomErrorIcon />
                      {errors[`nfc.source`]}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </FieldEditIndicator>
              <FieldEditIndicator
                fieldRef={nfcCustomValueRef}
                isFieldBeingEdited={isFieldBeingEdited}
                activeFieldRef={activeFieldRef}
                left="-40px"
                top="54px"
              >
                <FormControl
                  isDisabled={nfc.source !== 'custom'}
                  isInvalid={!!errors[`nfc.customValue`]}
                  onFocus={() => handleFocus('nfc.customValue')}
                >
                  <FormLabel variant="templateBuilderLabel">
                    Custom Value
                  </FormLabel>
                  <Input
                    value={nfc.customValue}
                    onChange={(value) =>
                      handleChange('nfc', 'customValue', value.target.value)
                    }
                  />
                  {errors[`nfc.customValue`] && (
                    <FormErrorMessage
                      pl={2}
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                    >
                      <CustomErrorIcon />
                      {errors[`nfc.customValue`]}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </FieldEditIndicator>
            </Flex>
          </Collapse>
          <OverrideRadio
            isVisible={!!isEdit && nfcAvailable && nfcChanged}
            value={overrideNfc}
            onChange={setOverrideNfc}
          />
        </Flex>

        <Divider borderColor="lightGrey" />

        <Flex direction="column" gap={4}>
          <FieldEditIndicator
            fieldRef={barcodeEnabledRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-40px"
            top="50px"
          >
            <FormControl
              isInvalid={!!errors[`barcode.enabled`]}
              onClick={() => handleFocus('barcode.enabled')}
            >
              <SwitchField
                value={barcode.enabled}
                onChange={(value) => {
                  handleChange('barcode', 'enabled', value);
                  handleFocus('barcode.enabled');
                }}
                label={
                  <FormLabel variant="templateBuilderLabel" mb={0}>
                    Enable Barcode
                  </FormLabel>
                }
              />
              {errors[`barcode.enabled`] && (
                <FormErrorMessage
                  pl={2}
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                >
                  <CustomErrorIcon />
                  {errors[`barcode.enabled`]}{' '}
                </FormErrorMessage>
              )}
            </FormControl>
          </FieldEditIndicator>
          <Collapse in={barcode.enabled}>
            <Flex direction="column" gap={4}>
              <FieldEditIndicator
                fieldRef={barcodeTypeRef}
                isFieldBeingEdited={isFieldBeingEdited}
                activeFieldRef={activeFieldRef}
                left="-40px"
                top="54px"
              >
                <FormControl
                  isInvalid={!!errors[`barcode.type`]}
                  onFocus={() => handleFocus('barcode.type')}
                >
                  <FormLabel variant="templateBuilderLabel">Type</FormLabel>
                  <Select
                    value={barcode.type}
                    onChange={(value) =>
                      handleChange('barcode', 'type', value.target.value)
                    }
                  >
                    {barcodeTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>
                  {errors[`barcode.type`] && (
                    <FormErrorMessage
                      pl={2}
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                    >
                      <CustomErrorIcon />
                      {errors[`barcode.type`]}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </FieldEditIndicator>
              <FieldEditIndicator
                fieldRef={barcodeSourceRef}
                isFieldBeingEdited={isFieldBeingEdited}
                activeFieldRef={activeFieldRef}
                left="-40px"
                top="54px"
              >
                <FormControl
                  isInvalid={!!errors[`barcode.source`]}
                  onFocus={() => handleFocus('barcode.source')}
                >
                  <FormLabel variant="templateBuilderLabel">Source</FormLabel>
                  <Select
                    value={barcode.source}
                    onChange={(value) =>
                      handleChange('barcode', 'source', value.target.value)
                    }
                  >
                    {sources.map((source) => (
                      <option key={source.value} value={source.value}>
                        {source.label}
                      </option>
                    ))}
                  </Select>
                  {errors[`barcode.source`] && (
                    <FormErrorMessage
                      pl={2}
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                    >
                      <CustomErrorIcon />
                      {errors[`barcode.source`]}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </FieldEditIndicator>
              <FieldEditIndicator
                fieldRef={barcodeValueRef}
                isFieldBeingEdited={isFieldBeingEdited}
                activeFieldRef={activeFieldRef}
                left="-40px"
                top="54px"
              >
                <FormControl
                  isDisabled={barcode.source !== 'custom'}
                  isInvalid={!!errors[`barcode.value`]}
                  onFocus={() => handleFocus('barcode.value')}
                >
                  <FormLabel variant="templateBuilderLabel">
                    Custom Value
                  </FormLabel>
                  <Input
                    value={barcode.value}
                    onChange={(value) =>
                      handleChange('barcode', 'value', value.target.value)
                    }
                  />
                  {errors[`barcode.value`] && (
                    <FormErrorMessage
                      pl={2}
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                    >
                      <CustomErrorIcon />
                      {errors[`barcode.value`]}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </FieldEditIndicator>
              <FieldEditIndicator
                fieldRef={barcodeDisplayTextRef}
                isFieldBeingEdited={isFieldBeingEdited}
                activeFieldRef={activeFieldRef}
                left="-40px"
                top="50px"
              >
                <FormControl
                  isInvalid={!!errors[`barcode.displayText`]}
                  onClick={() => handleFocus('barcode.displayText')}
                >
                  <SwitchField
                    value={barcode.displayText}
                    onChange={(value) =>
                      handleChange('barcode', 'displayText', value)
                    }
                    label={
                      <FormLabel variant="templateBuilderLabel" mb={0}>
                        Display Barcode Value
                      </FormLabel>
                    }
                  />
                  {errors[`barcode.displayText`] && (
                    <FormErrorMessage
                      pl={2}
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                    >
                      <CustomErrorIcon />
                      {errors[`barcode.displayText`]}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </FieldEditIndicator>
            </Flex>
          </Collapse>
          <OverrideRadio
            isVisible={!!isEdit && barcodeChanged}
            value={overrideBarcode}
            onChange={setOverrideBarcode}
          />
        </Flex>
      </Flex>
    </SectionWrapper>
  );
};

export default ScanningFeature;
